import {
  createContext,
  type PropsWithChildren,
  useContext,
  useMemo,
} from 'react';

import { useUser } from '@ll-platform/frontend/core/auth/hooks/useUser';
import { isUserInternal } from '@ll-platform/frontend/core/auth/utils/isInternal';
import { organizationService } from '@ll-platform/frontend/core/organization/async/OrganizationsService';
import type { Organization } from '@ll-platform/frontend/core/organization/types';

type ActiveOrganizationContextValue = {
  activeOrganization: Organization | null;
};

const ActiveOrganizationContext = createContext<ActiveOrganizationContextValue>(
  { activeOrganization: null },
);

export const ActiveOrganizationProvider = ({ children }: PropsWithChildren) => {
  const { activeUser } = useUser();

  const activeOrganization = useMemo<Organization | null>(() => {
    if (!activeUser || !isUserInternal(activeUser)) {
      return null;
    }

    const initialOrganizationId = organizationService.getOrganizationId();
    const organization = activeUser.organizations?.find(
      (org) => org.organization.id === initialOrganizationId,
    )?.organization;

    if (!organization) {
      if (
        initialOrganizationId &&
        organizationService.getIsBackdoorOrganization()
      ) {
        return organizationService.makeFakeOrganization(initialOrganizationId);
      }

      const firstOrganization = activeUser.organizations?.[0]?.organization;
      if (firstOrganization) {
        organizationService.setOrganizationId(firstOrganization.id);

        return firstOrganization;
      } else {
        organizationService.setOrganizationId(null);

        return null;
      }
    }

    return organization;
  }, [activeUser]);

  return (
    <ActiveOrganizationContext value={{ activeOrganization }}>
      {children}
    </ActiveOrganizationContext>
  );
};

export function useOptionalActiveOrganization() {
  const activeOrganization = useContext(ActiveOrganizationContext);

  return {
    activeOrganization: activeOrganization.activeOrganization ?? null,
  };
}

/**
 * Use only on internal pages with the OrganizationGuard
 */
export function useActiveOrganization() {
  const activeOrganization = useContext(
    ActiveOrganizationContext,
  ).activeOrganization;

  if (!activeOrganization) {
    throw new Error('Active organization is missing');
  }

  return {
    activeOrganization,
  };
}
